/**
 * Go Fish — server-authoritative rules engine, 4 players, one deal per match.
 *
 * Rules implemented:
 *  - One 52-card deck, five cards each, the rest face down as the stock.
 *  - On your turn you ask one other player (who holds cards) for a rank you hold yourself.
 *  - A hit: they hand over every card of that rank and you ask again.
 *  - A miss ("go fish"): you draw the top card of the stock. If it is the rank you asked for you ask again,
 *    otherwise the turn passes clockwise. With an empty stock a miss simply passes the turn.
 *  - Four cards of one rank are laid down at once as a book (also when they come from the deal or the stock).
 *  - A player with no cards draws a fresh hand from the stock when their turn comes (or is out if it is empty).
 *    A player whom nobody else can be asked draws from the stock instead.
 *  - The game ends when all 13 books are down, when no one can act, or after 12 fruitless asks in a row with an empty stock. Most books wins; ties go to the lower seat.
 */
import { buildDeck, parseCard, type CardId } from '../../core/cards.js';
import { checkInt, mergeKnown } from '../../core/config.js';
import { shuffle } from '../../core/rng.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  type EngineEvent,
  type GameEngine,
  type Standing,
} from '../../core/types.js';

export interface GoFishConfig {
  /** Cards dealt to each player (and drawn back when a hand runs out). */
  handSize: number;
}

export type GoFishPhase = 'ASK' | 'MATCH_END';

/** One public ask: who asked whom for which rank, and what came of it. */
export interface GoFishAsk {
  seat: number;
  target: number;
  rank: number;
  /** Cards handed over by the target. */
  got: number;
  /** True when the ask missed and the stock was drawn from. */
  fished: boolean;
  /** True when the drawn card was the asked rank (the asker goes again). */
  luck: boolean;
}

export interface GoFishState {
  config: GoFishConfig;
  players: string[];
  phase: GoFishPhase;
  hands: CardId[][];
  stock: CardId[];
  /** Ranks of the books each seat has laid down. */
  books: number[][];
  turn: number;
  /** The most recent asks, newest last. */
  log: GoFishAsk[];
  asks: number;
  /** Consecutive asks that changed nothing (a miss with an empty stock); a long run ends the game. */
  stall: number;
}

export type GoFishAction = { type: 'ask'; target: number; rank: number };

export interface GoFishLegal {
  /** Ranks the seat holds and may ask for. */
  ranks: number[];
  /** Seats that hold cards and may be asked. */
  targets: number[];
}

export interface GoFishView {
  gameType: 'GO_FISH';
  config: GoFishConfig;
  seat: number | null;
  phase: GoFishPhase;
  hand: CardId[];
  handCounts: number[];
  books: number[][];
  stock: number;
  turn: number;
  log: GoFishAsk[];
  totals: number[];
}

const DEFAULTS: GoFishConfig = { handSize: 5 };
const SEATS = 4;
const LOG_KEEP = 12;
const STALL_LIMIT = 12;

export const goFishRank = (card: CardId): number => parseCard(card).rank;

/** Cards ordered by rank (high to low) then suit, so equal ranks sit together. */
export function goFishSort(cards: readonly CardId[]): CardId[] {
  return [...cards].sort((a, b) => {
    const ca = parseCard(a);
    const cb = parseCard(b);
    return cb.rank - ca.rank || ca.suit.localeCompare(cb.suit);
  });
}

/** Rank → number of cards held. */
export function goFishCounts(hand: readonly CardId[]): Map<number, number> {
  const m = new Map<number, number>();
  for (const c of hand) m.set(goFishRank(c), (m.get(goFishRank(c)) ?? 0) + 1);
  return m;
}

/** Ranks you may ask for and seats you may ask, for a seat holding `hand`. */
export function goFishLegal(hand: readonly CardId[], handCounts: readonly number[], seat: number): GoFishLegal {
  const ranks = [...goFishCounts(hand).keys()].sort((a, b) => b - a);
  const targets = handCounts.map((n, s) => (s !== seat && n > 0 ? s : -1)).filter((s) => s >= 0);
  return { ranks, targets };
}

const booksTotal = (state: GoFishState) => state.books.reduce((n, b) => n + b.length, 0);

/** Lays down every complete rank in a hand. */
function layBooks(state: GoFishState, seat: number, events: EngineEvent[]): void {
  for (const [rank, n] of goFishCounts(state.hands[seat]!)) {
    if (n < 4) continue;
    state.hands[seat] = state.hands[seat]!.filter((c) => goFishRank(c) !== rank);
    state.books[seat]!.push(rank);
    events.push({ type: 'book', seat, rank });
  }
}

function draw(state: GoFishState, seat: number, n: number, events: EngineEvent[]): void {
  const taken = state.stock.splice(0, Math.min(n, state.stock.length));
  if (taken.length === 0) return;
  state.hands[seat] = goFishSort([...state.hands[seat]!, ...taken]);
  events.push({ type: 'draw', seat, count: taken.length });
  layBooks(state, seat, events);
}

function finish(state: GoFishState, events: EngineEvent[]): void {
  state.phase = 'MATCH_END';
  events.push({ type: 'matchEnd', standings: standings(state) });
}

/**
 * Brings the turn to a seat that can really act: refills empty hands, draws for a player with nobody to ask,
 * skips players who are out, and ends the game when nothing more can happen.
 */
function settle(state: GoFishState, events: EngineEvent[]): void {
  let skips = 0;
  for (let guard = 0; guard < 200; guard++) {
    if (booksTotal(state) >= 13 || state.stall >= STALL_LIMIT) return finish(state, events);
    const seat = state.turn;
    if (state.hands[seat]!.length === 0) draw(state, seat, state.config.handSize, events);
    if (state.hands[seat]!.length === 0) {
      if (++skips >= SEATS) return finish(state, events);
      state.turn = (seat + 1) % SEATS;
      continue;
    }
    const counts = state.hands.map((h) => h.length);
    if (goFishLegal(state.hands[seat]!, counts, seat).targets.length === 0) {
      if (state.stock.length > 0) {
        draw(state, seat, 1, events);
        skips = 0;
        continue;
      }
      if (++skips >= SEATS) return finish(state, events);
      state.turn = (seat + 1) % SEATS;
      continue;
    }
    return;
  }
  finish(state, events);
}

export const goFishEngine: GameEngine<GoFishConfig, GoFishState, GoFishAction, GoFishView, GoFishLegal> = {
  gameType: 'GO_FISH',
  kind: 'match',

  defaultConfig: () => ({ ...DEFAULTS }),

  normalizeConfig(input) {
    const c = mergeKnown(DEFAULTS, input);
    checkInt('handSize', c.handSize, 3, 7);
    return c;
  },

  seatLimits: () => ({ min: SEATS, max: SEATS }),

  createState(config, seats, rng) {
    assertEngine(seats.length === SEATS, 'INVALID_CONFIG', 'Go Fish needs exactly 4 seats');
    const players = Array<string>(SEATS).fill('');
    for (const s of seats) {
      assertEngine(s.seat >= 0 && s.seat < SEATS && !players[s.seat], 'SEAT_TAKEN', `Bad seat ${s.seat}`);
      players[s.seat] = s.playerId;
    }
    const deck = shuffle(buildDeck(), rng);
    const state: GoFishState = {
      config,
      players,
      phase: 'ASK',
      hands: [[], [], [], []],
      stock: [],
      books: [[], [], [], []],
      turn: rng.int(SEATS),
      log: [],
      asks: 0,
      stall: 0,
    };
    for (let s = 0; s < SEATS; s++) state.hands[s] = goFishSort(deck.splice(0, config.handSize));
    state.stock = deck;
    const events: EngineEvent[] = [];
    for (let s = 0; s < SEATS; s++) layBooks(state, s, events);
    settle(state, events);
    return state;
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    if (raw.type === 'ask' && Number.isInteger(raw.target) && Number.isInteger(raw.rank)) {
      return { type: 'ask', target: raw.target as number, rank: raw.rank as number };
    }
    throw new EngineError('INVALID_ACTION', 'Unknown Go Fish action');
  },

  applyAction(state, seat, action) {
    assertEngine(state.phase !== 'MATCH_END', 'GAME_OVER', 'The game is over');
    assertEngine(seat === state.turn, 'NOT_YOUR_TURN', 'Not your turn');
    const counts = state.hands.map((h) => h.length);
    const legal = goFishLegal(state.hands[seat]!, counts, seat);
    assertEngine(legal.ranks.includes(action.rank), 'ILLEGAL_MOVE', 'You can only ask for a rank you hold');
    assertEngine(legal.targets.includes(action.target), 'ILLEGAL_MOVE', 'That player cannot be asked');

    const events: EngineEvent[] = [];
    const given = state.hands[action.target]!.filter((c) => goFishRank(c) === action.rank);
    const ask: GoFishAsk = { seat, target: action.target, rank: action.rank, got: given.length, fished: false, luck: false };
    let again = false;
    if (given.length > 0) {
      state.hands[action.target] = state.hands[action.target]!.filter((c) => goFishRank(c) !== action.rank);
      state.hands[seat] = goFishSort([...state.hands[seat]!, ...given]);
      layBooks(state, seat, events);
      again = true;
    } else if (state.stock.length > 0) {
      ask.fished = true;
      const card = state.stock.shift()!;
      ask.luck = goFishRank(card) === action.rank;
      state.hands[seat] = goFishSort([...state.hands[seat]!, card]);
      layBooks(state, seat, events);
      again = ask.luck;
    }
    state.asks++;
    state.stall = given.length > 0 || ask.fished ? 0 : state.stall + 1;
    state.log.push(ask);
    if (state.log.length > LOG_KEEP) state.log.shift();
    events.push({ type: 'ask', ...ask });
    if (!again) state.turn = (seat + 1) % SEATS;
    settle(state, events);
    return events;
  },

  getActors(state) {
    return state.phase === 'ASK' ? [state.turn] : [];
  },

  getLegalActions(state, seat) {
    if (state.phase !== 'ASK' || seat !== state.turn) return { ranks: [], targets: [] };
    return goFishLegal(state.hands[seat]!, state.hands.map((h) => h.length), seat);
  },

  getTimeoutAction(state, seat) {
    const legal = goFishLegal(state.hands[seat]!, state.hands.map((h) => h.length), seat);
    const counts = goFishCounts(state.hands[seat]!);
    const rank = [...legal.ranks].sort((a, b) => (counts.get(b) ?? 0) - (counts.get(a) ?? 0) || b - a)[0]!;
    return { type: 'ask', target: legal.targets[0]!, rank };
  },

  getView(state, seat) {
    return {
      gameType: 'GO_FISH',
      config: state.config,
      seat,
      phase: state.phase,
      hand: seat === null ? [] : [...state.hands[seat]!],
      handCounts: state.hands.map((h) => h.length),
      books: state.books.map((b) => [...b]),
      stock: state.stock.length,
      turn: state.turn,
      log: state.log.map((a) => ({ ...a })),
      totals: state.books.map((b) => b.length),
    };
  },

  getSystemStep: () => null,

  runSystemStep: () => [],

  isFinished: (state) => state.phase === 'MATCH_END',

  getStandings(state): Standing[] {
    return standings(state);
  },

  getRoundsCompleted: (state) => state.asks,
};

/** Places by books laid down; equal books are split by seat order so every place is distinct. */
function standings(state: GoFishState): Standing[] {
  return [0, 1, 2, 3]
    .sort((a, b) => state.books[b]!.length - state.books[a]!.length || a - b)
    .map((seat, i) => ({ seat, place: i + 1, score: state.books[seat]!.length }));
}
